import { createClient } from '@supabase/supabase-js';
import { nanoid } from 'nanoid';
import { Location } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const storageBucket = import.meta.env.VITE_STORAGE_BUCKET;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const VALID_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const addLocation = async (location: Omit<Location, 'id'>): Promise<{ data: Location | null; error: string | null }> => {
  try {
    // Generate short_id
    const short_id = nanoid(8);

    const { data, error } = await supabase
      .from('locations')
      .insert([{
        name: location.name,
        description: location.description,
        short_description: location.shortDescription,
        category: location.category,
        coordinates: location.coordinates,
        images: location.images,
        tags: location.tags || [],
        status: 'pending',
        is_verified: false,
        short_id
      }])
      .select()
      .single();

    if (error) throw error;

    return {
      data: {
        id: data.id,
        name: data.name,
        description: data.description,
        shortDescription: data.short_description,
        category: data.category,
        coordinates: data.coordinates,
        images: data.images,
        tags: data.tags || [],
        status: data.status,
        created_at: data.created_at,
        updated_at: data.updated_at,
        is_verified: data.is_verified,
        search_vector: data.search_vector,
        short_id: data.short_id
      },
      error: null
    };
  } catch (error) {
    console.error('Error adding location:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Failed to add location'
    };
  }
};

export const uploadImage = async (file: File): Promise<string> => {
  try {
    // Validate file type
    if (!VALID_MIME_TYPES.includes(file.type)) {
      throw new Error('Недапушчальны тып файла. Дазволены толькі JPEG, PNG, GIF і WebP.');
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('Файл занадта вялікі. Максімальны памер - 5MB.');
    }

    // Generate unique filename with timestamp and original extension
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${timestamp}_${randomString}.${fileExt}`;

    // Upload file directly without any transformation
    const { error: uploadError, data } = await supabase.storage
      .from(storageBucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        contentType: file.type // Use the original file's MIME type
      });

    if (uploadError) {
      throw new Error(`Памылка загрузкі файла: ${uploadError.message}`);
    }

    if (!data?.path) {
      throw new Error('Не атрымалася атрымаць шлях да файла');
    }

    return data.path;
  } catch (error) {
    console.error('Error in uploadImage:', error);
    throw error;
  }
};

export const uploadImages = async (files: File[]): Promise<string[]> => {
  try {
    // Upload all files in parallel
    const uploadPromises = files.map(file => uploadImage(file));
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error in uploadImages:', error);
    throw error;
  }
};